import { LogOut } from 'lucide-react';
import { InitialsAvatar } from '@/shared/components/initials-avatar';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { TIER_TEXT_CLASS, tierTextStyle } from '../model/tier';

interface ParticipantSummaryProps {
  /** Nome completo (iniciais do avatar). */
  name: string;
  firstName: string;
  /** Cores do passbook — as mesmas do cabeçalho. */
  backgroundColor?: string;
  foregroundColor?: string;
  /** Nome do programa/loja, embaixo da saudação. */
  programName: string;
  /** Saldo já formatado no vocabulário da loja ("1.250 pontos"). Sem ele (Início, onde o cartão mostra), a faixa some. */
  balance?: string;
  /** Nível de fidelidade (pílula). */
  tierLabel?: string;
  tierColor?: string;
  onLogout: () => void;
  loggingOut?: boolean;
}

/** Topo da área logada: quem está aqui (avatar de iniciais), quanto tem e a saída. */
export function ParticipantSummary({
  name,
  firstName,
  backgroundColor,
  foregroundColor,
  programName,
  balance,
  tierLabel,
  tierColor,
  onLogout,
  loggingOut,
}: ParticipantSummaryProps) {
  return (
    <Card className="mx-auto mb-6 w-full max-w-xl p-4 sm:p-5">
      <div className="flex items-center gap-3 sm:gap-4">
        <InitialsAvatar
          name={name || firstName}
          backgroundColor={backgroundColor}
          color={foregroundColor}
          className="sm:size-14 sm:text-xl"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold sm:text-lg">Olá{firstName ? `, ${firstName}` : ''}!</p>
          <p className="truncate text-xs text-muted-foreground sm:text-sm">{programName}</p>
          {tierLabel ? (
            <span
              className={cn(
                'mt-1.5 inline-flex max-w-full truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider',
                TIER_TEXT_CLASS,
              )}
              style={{ borderColor: 'currentColor', ...tierTextStyle(tierColor) }}
            >
              {tierLabel}
            </span>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={onLogout}
          loading={loggingOut}
          aria-label="Sair da conta"
        >
          {!loggingOut ? <LogOut className="size-4" /> : null}
          <span className="hidden min-[360px]:inline">Sair</span>
        </Button>
      </div>
      {balance ? (
        <div className="mt-4 flex items-baseline justify-between gap-3 rounded-xl bg-muted/50 px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Seu saldo</span>
          <span className="truncate text-2xl font-semibold tabular-nums">{balance}</span>
        </div>
      ) : null}
    </Card>
  );
}
